import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, BookOpen } from 'lucide-react';
import { fmt } from '../../lib/accountingStore';
import { apiAccounts, ledgerApi } from '../../lib/api';
import { toast } from 'sonner';

const TYPES = ['Bank', 'Accounts Receivable', 'Other Current Asset', 'Fixed Asset', 'Accounts Payable', 'Credit Card', 'Other Current Liability', 'Long Term Liability', 'Equity', 'Income', 'Cost of Goods Sold', 'Expense', 'Other Income', 'Other Expense'];

const STANDARD = [
  { code: '1000', name: 'Operating Checking', type: 'Bank' },
  { code: '1010', name: 'Savings', type: 'Bank' },
  { code: '1100', name: 'Accounts Receivable', type: 'Accounts Receivable' },
  { code: '1200', name: 'Inventory – Chemicals', type: 'Other Current Asset' },
  { code: '1500', name: 'Vehicles & Equipment', type: 'Fixed Asset' },
  { code: '2000', name: 'Accounts Payable', type: 'Accounts Payable' },
  { code: '2100', name: 'Business Credit Card', type: 'Credit Card' },
  { code: '3000', name: 'Owner Equity', type: 'Equity' },
  { code: '4000', name: 'Service Revenue', type: 'Income' },
  { code: '5000', name: 'Chemicals & Supplies', type: 'Expense' },
  { code: '5100', name: 'Fuel', type: 'Expense' },
  { code: '5200', name: 'Payroll', type: 'Expense' },
  { code: '5300', name: 'Insurance', type: 'Expense' },
  { code: '5400', name: 'Office Supplies', type: 'Expense' },
  { code: '5500', name: 'Equipment', type: 'Expense' },
];

const typeColor = (t) => {
  if (t === 'Bank' || t === 'Accounts Receivable' || t.endsWith('Asset')) return 'bg-emerald-50 text-emerald-700';
  if (t.endsWith('Liability') || t === 'Credit Card' || t === 'Accounts Payable') return 'bg-orange-50 text-orange-700';
  if (t === 'Equity') return 'bg-purple-50 text-purple-700';
  if (t === 'Income' || t === 'Other Income') return 'bg-blue-50 text-blue-700';
  return 'bg-slate-100 text-slate-700';
};

export default function ChartOfAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [balances, setBalances] = useState({});
  const load = useCallback(() => {
    apiAccounts.list().then((d) => setAccounts(d || [])).catch(() => setAccounts([]));
    ledgerApi.balances().then((d) => setBalances(d.balances || {})).catch(() => setBalances({}));
  }, []);
  useEffect(() => { load(); }, [load]);
  const onDelete = async (id) => { try { await apiAccounts.remove(id); load(); toast.success('Account removed'); } catch { toast.error('Failed'); } };
  const loadStandard = async () => {
    try { await apiAccounts.bulk(STANDARD.map((a) => ({ ...a, id: `A-${a.code}`, balance: 0 }))); load(); toast.success('Standard chart of accounts added'); } catch { toast.error('Failed'); }
  };
  const groups = {};
  accounts.forEach((a) => { if (!groups[a.type]) groups[a.type] = []; groups[a.type].push(a); });

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-coa">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Chart of Accounts</h2><p className="text-slate-500 text-sm">{accounts.length} accounts organized by type.</p></div>
        <Button onClick={() => setOpen(true)} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="new-account-btn"><Plus className="w-4 h-4" />New Account</Button>
      </div>
      {accounts.length === 0 ? (
        <Card className="p-10 text-center border-slate-200">
          <BookOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">No accounts yet.</p>
          <Button onClick={loadStandard} className="mt-4 bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="load-standard-coa-btn">Load standard chart of accounts</Button>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <tr><th className="px-5 py-3 text-left">Code</th><th className="px-5 py-3 text-left">Name</th><th className="px-5 py-3 text-left">Type</th><th className="px-5 py-3 text-right">Balance</th><th className="px-5 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Object.entries(groups).map(([type, accs]) => (
                <React.Fragment key={type}>
                  <tr className="bg-slate-50/60"><td colSpan={5} className="px-5 py-2 text-xs font-bold text-slate-600 uppercase tracking-wider">{type}</td></tr>
                  {accs.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 font-mono text-slate-600">{a.code}</td>
                      <td className="px-5 py-3 font-semibold text-slate-900"><div className="flex items-center gap-2"><BookOpen className="w-3.5 h-3.5 text-slate-400" />{a.name}</div></td>
                      <td className="px-5 py-3"><Badge className={`${typeColor(a.type)} border-0`}>{a.type}</Badge></td>
                      <td className="px-5 py-3 text-right font-bold tabular-nums text-slate-900">{fmt(balances[a.id] ?? 0)}</td>
                      <td className="px-5 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => onDelete(a.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <AccountDialog open={open} setOpen={setOpen} onSave={load} />
    </div>
  );
}

function AccountDialog({ open, setOpen, onSave }) {
  const [code, setCode] = useState(''); const [name, setName] = useState(''); const [type, setType] = useState('Expense'); const [balance, setBalance] = useState('0');
  const submit = async () => {
    if (!name || !code) { toast.error('Name and code required'); return; }
    try { await apiAccounts.create({ id: `A-${Date.now().toString(36)}`, code, name, type, balance: 0 }); onSave(); setOpen(false); setCode(''); setName(''); setBalance('0'); toast.success('Account added'); } catch { toast.error('Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="account-dialog">
        <DialogHeader><DialogTitle>New Account</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3"><div><Label>Code</Label><Input value={code} onChange={(e) => setCode(e.target.value)} className="mt-1.5" placeholder="e.g. 5600" /></div><div className="text-xs text-slate-500 self-end pb-2">Balances come from the books. Enter an opening balance as a journal entry.</div></div>
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" placeholder="e.g. Subcontractors" /></div>
          <div><Label>Account Type</Label><Select value={type} onValueChange={setType}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="account-save-btn">Add Account</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
